// src/pages/admin/inhouse/PurchaseCreate.jsx
import React, { useContext, useEffect, useState, useCallback } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { SidebarContext } from "../../../context/Sidebar";
import { Row, Col, Card, Form, Button, Alert, Table, Badge } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { ButtonComponent } from "../../../components/elements";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Select from "react-select";
import { FiPlus, FiTrash2, FiSave, FiArrowLeft, FiDollarSign, FiInfo, FiUser, FiShoppingBag, FiCreditCard, FiAlertCircle } from "react-icons/fi";

export default function InHousePurchaseCreate() {
    const { t } = useContext(TranslatorContext);
    const { setSidebar } = useContext(SidebarContext);
    const navigate = useNavigate();
    const AuthToken = localStorage.getItem("authToken");

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [products, setProducts] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);
    const [selectedVendorDetails, setSelectedVendorDetails] = useState(null);

    const [formData, setFormData] = useState({
        vendor_id: null,
        purchase_date: '',
        purchase_number: '',
        notes: '',
        items: [],
        payments: []
    });

    // Fetch initial data
    useEffect(() => {
        setSidebar(false);
        fetchInitialData();
    }, [AuthToken, setSidebar]);

    const fetchInitialData = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/inhouse/purchases/create`, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            if (response.data.status) {
                setVendors(response.data.data.vendors || []);
                setProducts(response.data.data.products || []);
                setPaymentMethods(response.data.data.payment_methods || []);
                setFormData(prev => ({
                    ...prev,
                    purchase_number: response.data.data.purchase_number,
                    purchase_date: response.data.data.default_purchase_date,
                    items: [{ ...response.data.data.default_item }],
                    payments: [{ payment_method_id: '', amount: 0, payment_date: new Date().toISOString().split('T')[0] }]
                }));
            }
        } catch (err) {
            console.error("Error fetching initial data:", err);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: t('Failed to load form data'),
                showConfirmButton: false,
                timer: 2000
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch vendor details when selected
    const fetchVendorDetails = async (vendorId) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/vendors/${vendorId}/complete-ledger`, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });
            if (response.data.status) {
                setSelectedVendorDetails({
                    id: response.data.vendor.id,
                    name: response.data.vendor.name,
                    company_name: response.data.vendor.company_name,
                    email: response.data.vendor.email,
                    phone: response.data.vendor.phone,
                    total_purchases: response.data.summary.total_purchases || 0,
                    total_paid: response.data.summary.total_paid || 0,
                    balance: response.data.summary.total_balance || 0
                });
            }
        } catch (err) {
            console.error("Error fetching vendor details:", err);
        }
    };

    // Calculate line total including loose quantity
    const calculateLineTotal = (item, selectedProduct) => {
        const qty = parseFloat(item.purchase_qty) || 0;
        const price = parseFloat(item.purchase_price) || 0;
        const extraLooseQty = parseFloat(item.extra_loose_qty) || 0;

        // Calculate box/packet total
        const boxTotal = qty * price;

        // Calculate loose items value based on per-unit price
        let looseTotal = 0;
        if (selectedProduct && selectedProduct.unit_quantity > 0 && extraLooseQty > 0) {
            const unitPrice = price / selectedProduct.unit_quantity;
            looseTotal = extraLooseQty * unitPrice;
        }

        return boxTotal + looseTotal;
    };

    // Calculate total stock added (for display)
    const calculateTotalStockAdded = (item, selectedProduct) => {
        const qty = parseFloat(item.purchase_qty) || 0;
        const extraLooseQty = parseFloat(item.extra_loose_qty) || 0;
        const unitQuantity = selectedProduct?.unit_quantity || 1;

        return (qty * unitQuantity) + extraLooseQty;
    };

    // Calculate grand total
    const calculateGrandTotal = () => {
        return formData.items.reduce((sum, item) => {
            const selectedProduct = products.find(p => p.id === item.product_id);
            return sum + calculateLineTotal(item, selectedProduct);
        }, 0);
    };

    // Calculate total paid
    const calculateTotalPaid = () => {
        return formData.payments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
    };

    // Get remaining amount
    const getRemainingAmount = () => {
        return Math.max(0, calculateGrandTotal() - calculateTotalPaid());
    };

    // Calculate total after adding to vendor balance
    const getTotalWithVendorBalance = () => {
        const grandTotal = calculateGrandTotal();
        const vendorBalance = selectedVendorDetails?.balance || 0;
        return grandTotal + vendorBalance;
    };

    // Handle form field change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    // Handle vendor change
    const handleVendorChange = async (selectedOption) => {
        const vendorId = selectedOption ? selectedOption.value : null;
        setFormData(prev => ({ ...prev, vendor_id: vendorId }));
        if (errors.vendor_id) setErrors(prev => ({ ...prev, vendor_id: "" }));

        if (vendorId) {
            await fetchVendorDetails(vendorId);
            // Update first payment amount to vendor balance + new purchase total
            const grandTotal = calculateGrandTotal();
            const vendorBalance = selectedVendorDetails?.balance || 0;
            const totalAmount = grandTotal + vendorBalance;

            setFormData(prev => ({
                ...prev,
                payments: prev.payments.map((payment, idx) =>
                    idx === 0 ? { ...payment, amount: totalAmount } : payment
                )
            }));
        } else {
            setSelectedVendorDetails(null);
        }
    };

    // Handle item change
    const handleItemChange = (index, field, value) => {
        const updatedItems = [...formData.items];
        updatedItems[index][field] = value;

        // Auto-fill product details when product is selected
        if (field === 'product_id' && value) {
            const selectedProduct = products.find(p => p.id === value);
            if (selectedProduct) {
                updatedItems[index].purchase_price = selectedProduct.default_purchase_price;
                updatedItems[index].sale_price = selectedProduct.default_sale_price;
            }
        }

        setFormData(prev => ({ ...prev, items: updatedItems }));

        // Update payment amounts based on new total
        const newGrandTotal = updatedItems.reduce((sum, item) => {
            const selectedProduct = products.find(p => p.id === item.product_id);
            return sum + calculateLineTotal(item, selectedProduct);
        }, 0);

        const vendorBalance = selectedVendorDetails?.balance || 0;
        const totalWithBalance = newGrandTotal + vendorBalance;

        setFormData(prev => ({
            ...prev,
            payments: prev.payments.map((payment, idx) =>
                idx === 0 ? { ...payment, amount: totalWithBalance } : payment
            )
        }));
    };

    // Add new item row
    const addItemRow = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, {
                product_id: null,
                purchase_qty: 1,
                extra_loose_qty: 0,
                purchase_price: 0,
                sale_price: 0,
                expiry_date: null
            }]
        }));
    };

    // Remove item row
    const removeItemRow = (index) => {
        if (formData.items.length === 1) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t('At least one product is required'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }
        const updatedItems = formData.items.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, items: updatedItems }));
    };

    // Handle payment change
    const handlePaymentChange = (index, field, value) => {
        const updatedPayments = [...formData.payments];
        updatedPayments[index][field] = value;

        if (field === 'amount') {
            const newAmount = parseFloat(value) || 0;
            const totalWithBalance = getTotalWithVendorBalance();
            const otherPaymentsTotal = updatedPayments.reduce((sum, p, i) =>
                sum + (i !== index ? (parseFloat(p.amount) || 0) : 0), 0);

            if (otherPaymentsTotal + newAmount > totalWithBalance) {
                updatedPayments[index].amount = totalWithBalance - otherPaymentsTotal;
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'warning',
                    title: t('Amount adjusted to match total amount'),
                    showConfirmButton: false,
                    timer: 2000
                });
            }
        }

        setFormData(prev => ({ ...prev, payments: updatedPayments }));
    };

    // Add payment row
    const addPaymentRow = () => {
        const remaining = getRemainingAmount();
        if (remaining <= 0) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'info',
                title: t('Purchase is already fully paid'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }

        setFormData(prev => ({
            ...prev,
            payments: [...prev.payments, {
                payment_method_id: '',
                amount: remaining,
                payment_date: new Date().toISOString().split('T')[0],
                notes: null
            }]
        }));
    };

    // Remove payment row
    const removePaymentRow = (index) => {
        if (formData.payments.length === 1) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t('At least one payment row is required'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }
        const updatedPayments = formData.payments.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, payments: updatedPayments }));
    };

    // Validate and submit
    const handleSubmit = async () => {
        setSubmitting(true);
        setErrors({});
        setSubmitError(null);

        // Validation
        const validationErrors = {};
        if (!formData.vendor_id) validationErrors.vendor_id = t("Vendor is required");
        if (!formData.items.length) validationErrors.items = t("At least one product is required");

        formData.items.forEach((item, index) => {
            if (!item.product_id) validationErrors[`items.${index}.product_id`] = t("Product is required");
            if (!item.purchase_qty || item.purchase_qty <= 0) validationErrors[`items.${index}.purchase_qty`] = t("Valid quantity required");
            if (item.purchase_price === null || item.purchase_price === undefined || item.purchase_price < 0)
                validationErrors[`items.${index}.purchase_price`] = t("Valid price required");
        });

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setSubmitting(false);
            return;
        }

        const submitData = {
            vendor_id: formData.vendor_id,
            purchase_date: formData.purchase_date,
            purchase_number: formData.purchase_number,
            notes: formData.notes,
            items: formData.items.map(item => ({
                product_id: item.product_id,
                purchase_qty: parseFloat(item.purchase_qty) || 0,
                extra_loose_qty: parseFloat(item.extra_loose_qty) || 0,
                purchase_price: parseFloat(item.purchase_price) || 0,
                sale_price: parseFloat(item.sale_price) || 0,
                expiry_date: item.expiry_date || null
            })),
            payments: formData.payments
                .filter(p => p.payment_method_id && parseFloat(p.amount) > 0)
                .map(p => ({
                    payment_method_id: p.payment_method_id,
                    amount: parseFloat(p.amount) || 0,
                    payment_date: p.payment_date,
                    notes: p.notes
                }))
        };

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/inhouse/purchases`,
                submitData,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.status) {
                Swal.fire({
                    icon: 'success',
                    title: t('Purchase Created!'),
                    text: t('Purchase has been created successfully.'),
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('OK')
                });
                navigate("/inhouse/purchase");
            } else {
                setSubmitError(response.data.message || t('Failed to create purchase'));
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors);
            } else if (err.response?.data?.message) {
                setSubmitError(err.response.data.message);
            } else {
                setSubmitError(t('An error occurred while creating the purchase'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Vendor options
    const vendorOptions = vendors.map(v => ({ value: v.id, label: `${v.name} ${v.company_name ? `(${v.company_name})` : ''} ` }));
    const selectedVendor = vendorOptions.find(opt => opt.value === formData.vendor_id);

    // Product options for each row
    const getProductOptions = (currentIndex) => {
        const selectedProductIds = formData.items
            .filter((_, idx) => idx !== currentIndex && _.product_id)
            .map(_ => _.product_id);

        return products
            .filter(p => !selectedProductIds.includes(p.id))
            .map(p => ({ value: p.id, label: p.display_label }));
    };

    const grandTotal = calculateGrandTotal();
    const totalPaid = calculateTotalPaid();
    const remainingAmount = getRemainingAmount();
    const totalWithBalance = getTotalWithVendorBalance();

    if (loading) {
        return (
            <PageLayout>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status" />
                        <p className="mt-3 text-muted">{t("Loading...")}</p>
                    </div>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <div className="container-fluid px-0">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h4 className="mb-1">{t("Create New In-House Purchase")}</h4>
                        <p className="text-muted mb-0">{t("Add new in-house purchase order to inventory")}</p>
                    </div>
                    <Button
                        variant="secondary"
                        onClick={() => navigate("/inhouse/purchases")}
                        className="d-flex align-items-center gap-2"
                    >
                        <FiArrowLeft /> {t("Back to Purchases")}
                    </Button>
                </div>

                {submitError && (
                    <Alert variant="danger" className="mb-4" onClose={() => setSubmitError(null)} dismissible>
                        <Alert.Heading>{t("Error")}</Alert.Heading>
                        <p>{submitError}</p>
                    </Alert>
                )}

                <Row>
                    <Col lg={8}>
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Body className="p-4">
                                <h5 className="mb-4 pb-2 border-bottom">{t("Purchase Information")}</h5>

                                <Row className="g-4">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                {t("Vendor")} <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Select
                                                options={vendorOptions}
                                                value={selectedVendor}
                                                onChange={handleVendorChange}
                                                isClearable
                                                isSearchable
                                                placeholder={t("Select vendor...")}
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        borderColor: errors.vendor_id ? '#dc3545' : '#dee2e6',
                                                        minHeight: '38px'
                                                    })
                                                }}
                                            />
                                            {errors.vendor_id && (
                                                <div className="text-danger small mt-1">{errors.vendor_id}</div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">{t("Purchase Date")}</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="purchase_date"
                                                value={formData.purchase_date}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">{t("Notes")}</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                name="notes"
                                                value={formData.notes}
                                                onChange={handleChange}
                                                rows={3}
                                                placeholder={t("Additional notes...")}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <h5 className="mt-4 mb-4 pb-2 border-bottom">{t("Products")}</h5>

                                {formData.items.map((item, index) => {
                                    const productOptions = getProductOptions(index);
                                    const selectedProduct = products.find(p => p.id === item.product_id);
                                    const lineTotal = calculateLineTotal(item, selectedProduct);
                                    const totalStockAdded = calculateTotalStockAdded(item, selectedProduct);
                                    const unitPrice = selectedProduct && selectedProduct.unit_quantity > 0
                                        ? (item.purchase_price / selectedProduct.unit_quantity)
                                        : 0;

                                    return (
                                        <div key={index} className="border rounded p-3 mb-3 bg-light">
                                            <Row className="g-3">
                                                <Col lg={4} md={6}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-bold">
                                                            {t("Product")} <span className="text-danger">*</span>
                                                        </Form.Label>
                                                        <Select
                                                            options={productOptions}
                                                            value={productOptions.find(opt => opt.value === item.product_id)}
                                                            onChange={(opt) => handleItemChange(index, 'product_id', opt?.value || null)}
                                                            isClearable
                                                            isSearchable
                                                            placeholder={t("Select product...")}
                                                            className="react-select-container-sm"
                                                            classNamePrefix="react-select"
                                                        />
                                                        {errors[`items.${index}.product_id`] && (
                                                            <div className="text-danger small mt-1">{errors[`items.${index}.product_id`]}</div>
                                                        )}
                                                    </Form.Group>
                                                </Col>

                                                <Col lg={2} md={3}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-bold">
                                                            {t("Purchase Unit")}
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            value={item.purchase_qty}
                                                            onChange={(e) => handleItemChange(index, 'purchase_qty', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                                            step="0.01"
                                                            min="0.01"
                                                            placeholder={t("Enter quantity")}
                                                            isInvalid={!!errors[`items.${index}.purchase_qty`]}
                                                        />
                                                        <Form.Text className="text-muted">
                                                            {t("Number of purchase units")}
                                                        </Form.Text>
                                                    </Form.Group>
                                                </Col>

                                                <Col lg={2} md={3}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-bold">
                                                            {t("Extra Loose Qty")}
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            value={item.extra_loose_qty}
                                                            onChange={(e) => handleItemChange(index, 'extra_loose_qty', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                                            step="0.01"
                                                            min="0"
                                                            placeholder={t("Enter loose quantity")}
                                                        />
                                                        <Form.Text className="text-muted">
                                                            {t("Additional loose items")}
                                                        </Form.Text>
                                                    </Form.Group>
                                                </Col>

                                                <Col lg={2} md={3}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-bold">
                                                            {t("Price Per Box")}
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            value={item.purchase_price}
                                                            onChange={(e) => handleItemChange(index, 'purchase_price', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                                            step="0.01"
                                                            min="0"
                                                            placeholder={t("Enter price")}
                                                            isInvalid={!!errors[`items.${index}.purchase_price`]}
                                                        />
                                                        {selectedProduct && selectedProduct.unit_quantity > 0 && (
                                                            <Form.Text className="text-muted">
                                                                {t("Unit Price")}: {unitPrice}/{selectedProduct.unit_type}
                                                            </Form.Text>
                                                        )}
                                                    </Form.Group>
                                                </Col>

                                                <Col lg={2} md={3}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-bold">
                                                            {t("Total")}
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            value={lineTotal.toFixed(2)}
                                                            readOnly
                                                            className="bg-white"
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col lg={12}>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => removeItemRow(index)}
                                                        className="float-end"
                                                    >
                                                        <FiTrash2 className="me-1" />
                                                        {t("Remove Product")}
                                                    </Button>
                                                </Col>

                                                {/* Stock and Financial Breakdown */}
                                                {selectedProduct && (
                                                    <Col md={12}>
                                                        <div className="bg-info rounded p-3 mt-2">
                                                            <Row>
                                                                <Col md={6}>
                                                                    <small className="text-dark">
                                                                        <strong>{t("Stock Calculation")}:</strong><br />
                                                                        {item.purchase_qty || 0} {selectedProduct.purchase_unit} × {selectedProduct.unit_quantity}{selectedProduct.unit_type} = {(item.purchase_qty || 0) * selectedProduct.unit_quantity} {selectedProduct.unit_type}
                                                                        {item.extra_loose_qty > 0 && (
                                                                            <>
                                                                                <br />
                                                                                + {item.extra_loose_qty} {t("loose")} {selectedProduct.unit_type}
                                                                                <br />
                                                                                <strong className="text-success">
                                                                                    = {totalStockAdded} {selectedProduct.unit_type} {t("total stock added")}
                                                                                </strong>
                                                                            </>
                                                                        )}
                                                                        {item.extra_loose_qty <= 0 && (
                                                                            <strong className="text-success">
                                                                                <br />= {totalStockAdded} {selectedProduct.unit_type} {t("total stock added")}
                                                                            </strong>
                                                                        )}
                                                                    </small>
                                                                </Col>
                                                            </Row>
                                                        </div>
                                                    </Col>
                                                )}
                                            </Row>
                                        </div>
                                    );
                                })}

                                <Button
                                    variant="primary"
                                    onClick={addItemRow}
                                    className="d-flex align-items-center gap-2 mt-2"
                                >
                                    <FiPlus /> {t("Add Product")}
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={4}>
                        {/* Vendor Balance Card - Shows when vendor is selected */}
                        {selectedVendorDetails && (
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center gap-3 mb-3">
                                        <div className="rounded-circle bg-primary bg-opacity-10 p-3">
                                            <FiUser className="text-primary fs-4" />
                                        </div>
                                        <div>
                                            <h5 className="mb-0">{selectedVendorDetails.name}</h5>
                                            {selectedVendorDetails.company_name && (
                                                <small className="text-muted">{selectedVendorDetails.company_name}</small>
                                            )}
                                        </div>
                                    </div>

                                    <div className="border-top pt-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="text-muted">{t("Previous Purchases")}:</span>
                                            <strong>Rs. {selectedVendorDetails.total_purchases?.toLocaleString()}</strong>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="text-muted">{t("Previous Paid")}:</span>
                                            <strong className="text-success">Rs. {selectedVendorDetails.total_paid?.toLocaleString()}</strong>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                            <span className="fw-bold">{t("Previous Balance")}:</span>
                                            <strong className={`${selectedVendorDetails.balance > 0 ? 'text-danger' : 'text-success'} fs-5`}>
                                                Rs. {selectedVendorDetails.balance?.toLocaleString()}
                                            </strong>
                                        </div>
                                    </div>

                                    <hr />

                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="text-muted">{t("Current Purchase Total")}:</span>
                                        <strong>Rs. {grandTotal.toLocaleString()}</strong>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                        <span className="fw-bold">{t("Total Payable (Including Previous Balance)")}:</span>
                                        <strong className="text-primary fs-5">
                                            {totalWithBalance.toLocaleString()}
                                        </strong>
                                    </div>

                                    {selectedVendorDetails.balance > 0 && (
                                        <Alert variant="warning" className="mt-3 mb-0 small">
                                            <FiAlertCircle className="me-1" />
                                            {t("This vendor has a previous balance of")} {selectedVendorDetails.balance.toLocaleString()}
                                        </Alert>
                                    )}
                                </Card.Body>
                            </Card>
                        )}

                        {/* Purchase Summary Card */}
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Body className="p-4">
                                <h5 className="mb-4 pb-2 border-bottom">{t("Purchase Summary")}</h5>

                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted">{t("Total Items")}:</span>
                                    <strong>{formData.items.length}</strong>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted">{t("Sub Total")}:</span>
                                    <strong>Rs. {grandTotal.toLocaleString()}</strong>
                                </div>
                                {selectedVendorDetails && selectedVendorDetails.balance > 0 && (
                                    <div className="d-flex justify-content-between mb-2 text-warning">
                                        <span>{t("Previous Balance")}:</span>
                                        <strong>Rs. {selectedVendorDetails.balance.toLocaleString()}</strong>
                                    </div>
                                )}
                                <div className="d-flex justify-content-between mb-3 pb-3 border-bottom">
                                    <span className="text-muted">{t("Grand Total")}:</span>
                                    <strong className="text-primary h5">Rs. {totalWithBalance.toLocaleString()}</strong>
                                </div>

                                <div className="d-flex justify-content-between mb-2 text-success">
                                    <span>{t("Total Paid")}:</span>
                                    <strong>Rs. {totalPaid.toLocaleString()}</strong>
                                </div>
                                <div className="d-flex justify-content-between mb-3 text-danger">
                                    <span>{t("Due Amount")}:</span>
                                    <strong>Rs. {remainingAmount.toLocaleString()}</strong>
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Payments Card */}
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                                    <h5 className="mb-0">{t("Payments")}</h5>
                                    <Button
                                        variant="success"
                                        size="sm"
                                        onClick={addPaymentRow}
                                        disabled={remainingAmount <= 0}
                                        className="d-flex align-items-center gap-1"
                                    >
                                        <FiPlus size={14} /> {t("Add Payment")}
                                    </Button>
                                </div>

                                {formData.payments.map((payment, index) => (
                                    <div key={index} className="border rounded p-3 mb-3">
                                        <Row className="g-2">
                                            <Col xs={12}>
                                                <Form.Group>
                                                    <Form.Label className="small fw-bold">{t("Method")}</Form.Label>
                                                    <Form.Select
                                                        size="sm"
                                                        value={payment.payment_method_id}
                                                        onChange={(e) =>
                                                            handlePaymentChange(index, 'payment_method_id', e.target.value)
                                                        }
                                                    >
                                                        <option value="">{t("Select method")}</option>
                                                        {paymentMethods
                                                            .filter(pm => {
                                                                const selectedPaymentMethods = formData.payments
                                                                    .filter((_, i) => i !== index)
                                                                    .map(p => String(p.payment_method_id));

                                                                return !selectedPaymentMethods.includes(String(pm.id));
                                                            })
                                                            .map(pm => (
                                                                <option key={pm.id} value={pm.id}>
                                                                    {pm.name}
                                                                </option>
                                                            ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col xs={6}>
                                                <Form.Group>
                                                    <Form.Label className="small fw-bold">{t("Amount")}</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        size="sm"
                                                        value={payment.amount}
                                                        onChange={(e) => handlePaymentChange(index, 'amount', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                                        step="0.01"
                                                        min="0"
                                                        placeholder={t("Enter amount")}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={6}>
                                                <Form.Group>
                                                    <Form.Label className="small fw-bold">{t("Date")}</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        size="sm"
                                                        value={payment.payment_date}
                                                        onChange={(e) => handlePaymentChange(index, 'payment_date', e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            {formData.payments.length > 1 && (
                                                <Col xs={12} className="text-end">
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        className="text-danger p-0"
                                                        onClick={() => removePaymentRow(index)}
                                                    >
                                                        <FiTrash2 /> {t("Remove")}
                                                    </Button>
                                                </Col>
                                            )}
                                        </Row>
                                    </div>
                                ))}

                                <hr />
                                <div className="d-flex justify-content-between">
                                    <strong>{t("Total Payments")}:</strong>
                                    <strong className="text-success">Rs. {totalPaid.toLocaleString()}</strong>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Submit Button */}
                <div className="mt-4 d-flex gap-3 justify-content-end">
                    <Button
                        variant="light"
                        onClick={() => navigate("/inhouse/purchases")}
                        className="px-4"
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="px-4 d-flex align-items-center gap-2"
                    >
                        {submitting ? (
                            <>
                                <div className="spinner-border spinner-border-sm" role="status" />
                                <span>{t("Creating...")}</span>
                            </>
                        ) : (
                            <>
                                <FiSave />
                                <span>{t("Create Purchase")}</span>
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </PageLayout>
    );
}